const WEBP_TYPE = 'image/webp';
const JPEG_TYPE = 'image/jpeg';
const DEFAULT_IMAGE_LOAD_TIMEOUT_MS = 15000;

let _supportsWebP = null;

function supportsWebP() {
    if (_supportsWebP !== null) return _supportsWebP;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        _supportsWebP = canvas.toDataURL(WEBP_TYPE).startsWith('data:image/webp');
    } catch {
        _supportsWebP = false;
    }
    return _supportsWebP;
}

function loadImage(file, { timeoutMs = DEFAULT_IMAGE_LOAD_TIMEOUT_MS } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        let finished = false;
        const cleanup = () => {
            if (finished) return;
            finished = true;
            clearTimeout(timer);
            URL.revokeObjectURL(url);
        };
        const timer = setTimeout(() => {
            cleanup();
            reject(new Error('Image load timed out'));
        }, timeoutMs);
        img.onload = () => {
            cleanup();
            resolve(img);
        };
        img.onerror = () => {
            cleanup();
            reject(new Error('Failed to load image'));
        };
        img.src = url;
    });
}

/**
 * Resize an image to exact target dimensions using crop-to-fill.
 * Centers the source, crops the overflow, and outputs in WebP (JPEG fallback).
 *
 * @param {File|Blob} file - Source image
 * @param {Object} options
 * @param {number} options.width  - Target width  (default: 300)
 * @param {number} options.height - Target height (default: 300)
 * @param {number} options.quality - Output quality 0-1 (default: 0.8)
 * @param {'cover'|'contain'} options.mode - 'cover' crops to fill, 'contain' fits inside (default: 'cover')
 * @returns {Promise<Blob>} - Resized image blob
 */
export async function resizeForThumbnail(file, { width = 300, height = 300, quality = 0.8, mode = 'cover' } = {}) {
    if (!file || !(file instanceof Blob)) throw new Error('resizeForThumbnail requires a File or Blob');

    const img = await loadImage(file);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const srcRatio = img.width / img.height;
    const dstRatio = width / height;

    if (mode === 'cover') {
        let sx, sy, sw, sh;
        if (srcRatio > dstRatio) {
            sh = img.height;
            sw = img.height * dstRatio;
            sx = (img.width - sw) / 2;
            sy = 0;
        } else {
            sw = img.width;
            sh = img.width / dstRatio;
            sx = 0;
            sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);
    } else {
        let dw, dh;
        if (srcRatio > dstRatio) {
            dw = width;
            dh = width / srcRatio;
        } else {
            dh = height;
            dw = height * srcRatio;
        }
        const dx = (width - dw) / 2;
        const dy = (height - dh) / 2;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, img.width, img.height, dx, dy, dw, dh);
    }

    const mimeType = supportsWebP() ? WEBP_TYPE : JPEG_TYPE;

    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => resolve(blob || new Blob([], { type: mimeType })),
            mimeType,
            quality
        );
    });
}

/**
 * Compress an image file before uploading to Firebase Storage.
 * Resizes to maxWidth/maxHeight while maintaining aspect ratio,
 * and outputs as JPEG at the specified quality.
 *
 * @param {File} file - The original image file
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width in pixels (default: 1200)
 * @param {number} options.maxHeight - Maximum height in pixels (default: 1200)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, { maxWidth = 1200, maxHeight = 1200, quality = 0.8 } = {}) {
    // Skip non-image files
    if (!file || !file.type.startsWith('image/')) return file;

    // Skip small files (under 200KB) — already small enough
    if (file.size < 200 * 1024) return file;

    // Skip SVGs and GIFs (can't usefully compress)
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

    // HEIC/HEIF frequently fail to decode in browser canvas pipelines.
    if (file.type === 'image/heic' || file.type === 'image/heif') return file;

    try {
        const img = await loadImage(file);
        return await new Promise((resolve) => {
            let { width, height } = img;

            // If already within bounds, skip resize but still re-encode for compression
            if (width <= maxWidth && height <= maxHeight && file.size < 500 * 1024) {
                resolve(file);
                return;
            }

            // Calculate new dimensions maintaining aspect ratio
            if (width > maxWidth) {
                height = Math.round(height * (maxWidth / width));
                width = maxWidth;
            }
            if (height > maxHeight) {
                width = Math.round(width * (maxHeight / height));
                height = maxHeight;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) { resolve(file); return; }
                    // If compressed version is larger than original, use original
                    if (blob.size >= file.size) { resolve(file); return; }
                    const compressed = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
                    resolve(compressed);
                },
                'image/jpeg',
                quality
            );
        });
    } catch {
        return file; // Fallback to original on decode/timeout errors
    }
}
